import { UserFacingErrorMessage } from '$lib/lib/errors';
import { waitForAuthState } from '$lib/platform/auth-provider';
import { firebaseApp } from '$lib/firebase/client';

/**
 * The book download goes straight to the function, not through Hosting's
 * /api/pdf-download rewrite: Hosting does not stream function responses and
 * cuts them off after 60 s, which truncates a 51-65 MB book. The emulator
 * build keeps the rewrite (the hosting emulator has neither limit).
 */
function pdfDownloadUrl(): string {
    const projectId = firebaseApp?.options.projectId;
    if (import.meta.env.VITE_USE_EMULATORS === 'true' || !projectId) return '/api/pdf-download';
    return `https://europe-west1-${projectId}.cloudfunctions.net/pdfDownload_fv2`;
}

/** Same reason as pdfDownloadUrl: the whole book is too big for Hosting's function rewrite. */
function bookFileUrl(pdfId: string): string {
    const projectId = firebaseApp?.options.projectId;
    const path = `/api/v1/books/${pdfId}/file`;
    if (import.meta.env.VITE_USE_EMULATORS === 'true' || !projectId) return path;
    return `https://europe-west1-${projectId}.cloudfunctions.net/v1_fv2${path}`;
}

export class HttpError extends Error {
    constructor(public status: number, message: string, public errors: string[] = []) {
        super(message);
    }
}

export class BaseApiService {
    private static instance: BaseApiService | null = null;
    private baseUrl: string;
    private idToken: string;
    private static waitingRequests: ((value: BaseApiService | PromiseLike<BaseApiService>) => void)[] = [];

    constructor(idToken: string) {
        this.baseUrl = "";
        this.idToken = idToken;
    }

    public get BaseUrl(): string {
        return this.baseUrl;
    }

    public get IdToken(): string {
        return this.idToken;
    }

    static initialize(idToken: string): void {
        BaseApiService.instance = new BaseApiService(idToken);
        BaseApiService.waitingRequests.forEach(resolve => resolve(BaseApiService.instance!));
        BaseApiService.waitingRequests = [];
    }

    /** Test-only: drop the singleton so getInstance waits again. */
    static resetForTests(): void {
        BaseApiService.instance = null;
        BaseApiService.waitingRequests = [];
    }

    static async getInstance(): Promise<BaseApiService> {
        if (BaseApiService.instance) {
            return BaseApiService.instance;
        }

        const user = await waitForAuthState();

        if (BaseApiService.instance) {
            return BaseApiService.instance;
        }

        if (!user) {
            throw new UserFacingErrorMessage("errors.sign_in_required", { code: "sign_in_required" });
        }

        // Auth is known and a user exists: the token should arrive shortly via
        // initialize(). Keep the timeout so a failed token fetch cannot hang forever.
        return new Promise((resolve, reject) => {
            if (BaseApiService.instance) {
                resolve(BaseApiService.instance);
                return;
            }
            const timeout = setTimeout(() => {
                BaseApiService.waitingRequests = BaseApiService.waitingRequests.filter((r) => r !== onReady);
                reject(new UserFacingErrorMessage("errors.timeout", { code: "timeout" }));
            }, 10000);
            const onReady = (instance: BaseApiService | PromiseLike<BaseApiService>) => {
                clearTimeout(timeout);
                resolve(instance);
            };
            BaseApiService.waitingRequests.push(onReady);
        });
    }

    private defaultOptions(options: RequestInit): RequestInit {
        const opts: RequestInit = {
            method: "GET",
            signal: options.signal,
            ...options,
            headers: {
                Authorization: `Bearer ${this.idToken}`,
                ...options.headers,
            },
        };

        if (opts.body && !(opts.body instanceof FormData)) {
            (opts.headers as Record<string, string>)["Content-Type"] = "application/json";
        }

        return opts;
    }

    async execute(url: string, options: RequestInit & { skipAuth?: boolean }): Promise<any> {
        if (!options.skipAuth && !this.idToken) {
            throw new Error("No idToken set");
        }
        const response = await fetch(`${this.baseUrl}${url}`, this.defaultOptions(options));
        if (!response.ok) {
            throw new Error(await response.text());
        }
        const contentType = response.headers.get("Content-Type");
        if (contentType && contentType.includes("image/png")) {
            return await response.blob();
        }
        if (contentType && contentType.includes("application/pdf")) {
            return await response.arrayBuffer();
        }
        if (contentType && contentType.includes("application/octet-stream")) {
            return await response.arrayBuffer();
        }
        if (contentType && contentType.includes("application/json")) {
            return await response.json();
        }
        return await response.text();
    }
}

export class SubscriptionApiService {
    static async syncSubscriptions(impersonateUser: string | null = null): Promise<any> {
        const headers: Record<string, string> = {};
        if (impersonateUser) {
            headers["X-Impersonate-User"] = impersonateUser;
        }
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/sync-subscription", {
            method: "PUT",
            headers
        });
    }

    static async cancelSubscription(subscriptionId: string, customerId: string, impersonateUser: string | null = null): Promise<any> {
        const headers: Record<string, string> = {};
        if (impersonateUser) {
            headers["X-Impersonate-User"] = impersonateUser;
        }
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/cancel-subscription", {
            method: "PUT",
            headers,
            body: JSON.stringify({ subscriptionId, customerId })
        });
    }
}

export class PaymentApiService {
    static async cancelPendingSubscription(referenceId: string): Promise<{ canceled: boolean }> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/cancel-pending-subscription", {
            method: "POST",
            body: JSON.stringify({ referenceId })
        });
    }

    static async syncPayments(impersonateUser: string | null = null): Promise<any> {
        const headers: Record<string, string> = {};
        if (impersonateUser) {
            headers["X-Impersonate-User"] = impersonateUser;
        }
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/sync-payments", {
            method: "PUT",
            headers
        });
    }

    static async setInvoiceNr(paymentId: string, impersonateUser: string | null = null): Promise<any> {
        const headers: Record<string, string> = {};
        if (impersonateUser) {
            headers["X-Impersonate-User"] = impersonateUser;
        }
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/payments/set-invoice-nr", {
            method: "PUT",
            headers,
            body: JSON.stringify({ paymentId })
        });
    }
}

export class VoicePlanApiService {
    /**
     * Copy the caller's users/{uid}.voicePlans entry for a legacy plan into the
     * plan's userIds/editorIds, so the lessons stay readable once the plan is
     * strict. The server ignores strict plans and plans the caller has no entry for.
     */
    static async ensureMembership(voicePlanId: string): Promise<{ updated: boolean }> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/voice-plans/ensure-membership", {
            method: "POST",
            body: JSON.stringify({ voicePlanId })
        });
    }
}

export class AccountApiService {
    static async isMigrated(email: string): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/is-migrated", {
            method: "POST",
            body: JSON.stringify({ email }),
            skipAuth: true,
        });
    }

    static async setIsMigrated(impersonateUser: string | null = null): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        const headers: Record<string, string> = {};
        if (impersonateUser) {
            headers["X-Impersonate-User"] = impersonateUser;
        }
        return await apiInstance.execute("/api/v1/set-is-migrated", {
            method: "PUT",
            headers,
        });
    }
}

export class PdfApiService {
    /** Single-use ticket (valid 60 s) for one `downloadPdf` call; 403 when the user does not own the book. */
    static async getPdfDownloadToken(pdfId: string): Promise<{
        ticket: string;
        version: string;
        fileSize: number;
    }> {
        const apiInstance = await BaseApiService.getInstance();
        const url = `${apiInstance["baseUrl"]}/api/v1/get-pdf-download-token/${pdfId}`;
        const response = await fetch(url, {
            headers: { Authorization: `Bearer ${apiInstance["idToken"]}` },
        });
        if (!response.ok) throw new HttpError(response.status, await response.text());
        return response.json();
    }

    /** Downloads the whole PDF for a ticket. The response has no Content-Length, so progress reports bytes loaded. */
    static async downloadPdf(ticket: string, onProgress?: (loaded: number) => void): Promise<ArrayBuffer> {
        const response = await fetch(pdfDownloadUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ticket }),
        });
        if (!response.ok) throw new HttpError(response.status, await response.text());
        if (!response.body) return await response.arrayBuffer();

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let loaded = 0;
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            loaded += value.length;
            onProgress?.(loaded);
        }
        const result = new Uint8Array(loaded);
        let offset = 0;
        for (const chunk of chunks) {
            result.set(chunk, offset);
            offset += chunk.length;
        }
        return result.buffer;
    }
}

/** Exercise links of a book (ADR 0019); ADMIN or EDIT_CONTENT only. */
export interface BookChapter { title: string; startPage: number; endPage?: number }

export class BookLinksApiService {
    private static async call(path: string, init: RequestInit = {}): Promise<Response> {
        const api = await BaseApiService.getInstance();
        const headers: Record<string, string> = { Authorization: `Bearer ${api.IdToken}` };
        if (init.body) headers["Content-Type"] = "application/json";
        const response = await fetch(path.startsWith("http") ? path : `${api.BaseUrl}${path}`, { ...init, headers });
        if (!response.ok) {
            const text = await response.text();
            let errors: string[] = [];
            try { errors = JSON.parse(text).errors ?? []; } catch { /* not JSON */ }
            throw new HttpError(response.status, text, errors);
        }
        return response;
    }

    static async getLinks(pdfId: string): Promise<{ version: string | null; links: any[]; chapters: BookChapter[]; chaptersEditable: boolean }> {
        return (await this.call(`/api/v1/books/${pdfId}/links`)).json();
    }

    /** Throws an HttpError whose `errors` lists the validation errors on a 400. */
    static async saveLinks(pdfId: string, links: unknown[]): Promise<void> {
        await this.call(`/api/v1/books/${pdfId}/links`, { method: "PUT", body: JSON.stringify({ links }) });
    }

    static async detect(pdfId: string): Promise<{ jobId: string }> {
        return (await this.call(`/api/v1/books/${pdfId}/links/detect`, { method: "POST" })).json();
    }

    /** ADMIN only: makes the file uploaded to bookUploads/{pdfId}/{uploadId}.pdf the current book. */
    static async publish(pdfId: string, uploadId: string): Promise<{ version: string; pages: number; jobId: string }> {
        return (await this.call(`/api/v1/admin/books/${pdfId}/publish`, { method: "POST", body: JSON.stringify({ uploadId }) })).json();
    }

    /** ADMIN only; throws an HttpError whose `errors` lists the validation errors on a 400. */
    static async saveChapters(pdfId: string, chapters: BookChapter[]): Promise<void> {
        await this.call(`/api/v1/admin/books/${pdfId}/chapters`, { method: "PUT", body: JSON.stringify({ chapters }) });
    }

    /** The plain bundled book (no footer, no stamped links). */
    static async getFile(pdfId: string): Promise<ArrayBuffer> {
        return (await this.call(bookFileUrl(pdfId))).arrayBuffer();
    }
}

export class EntitlementApiService {
    /**
     * Initiates a purchase for a given entitlement. Returns a Mollie checkout URL.
     * @param entitlementKey The key of the entitlement to purchase
     */
    static async purchaseEntitlement(entitlementKey: string): Promise<{ checkoutUrl: string }> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/entitlements/purchase", {
            method: "POST",
            body: JSON.stringify({ entitlementKey })
        });
    }

    static async cancelPendingEntitlement(referenceId: string): Promise<{ canceled: boolean }> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/cancel-pending-entitlement", {
            method: "POST",
            body: JSON.stringify({ referenceId })
        });
    }

    static async getUserEntitlements(): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/entitlements", {
            method: "GET"
        });
    }
}

export class AdminApiService {
    static async impersonate(targetUid: string | null): Promise<{ customToken: string }> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/admin/impersonate", {
            method: "POST",
            body: JSON.stringify({ targetUid })
        });
    }
}

export class OrganizationApiService {
    static async createOrganization(name: string, seats: number, interval: string): Promise<string> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/organization", {
            method: "POST",
            body: JSON.stringify({ name, seats, interval })
        });
    }

    static async getOrganization(orgId: string): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}`, {
            method: "GET"
        });
    }

    static async updateSeats(orgId: string, seats: number): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}/seats`, {
            method: "PUT",
            body: JSON.stringify({ seats })
        });
    }

    static async addMember(orgId: string, email: string): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}/members`, {
            method: "POST",
            body: JSON.stringify({ email })
        });
    }

    static async revokeMember(orgId: string, uid: string): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}/members/${uid}`, {
            method: "DELETE"
        });
    }

    static async addAdmin(orgId: string, email: string): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}/admins`, {
            method: "POST",
            body: JSON.stringify({ email })
        });
    }

    static async removeAdmin(orgId: string, uidOrEmail: string): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}/admins/${uidOrEmail}`, {
            method: "DELETE"
        });
    }

    static async createEnterpriseOrganization(name: string, seats: number, adminEmail: string): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute("/api/v1/admin/organization", {
            method: "POST",
            body: JSON.stringify({ name, seats, adminEmail })
        });
    }

    static async updateOrganizationStatus(orgId: string, status: "active" | "suspended" | "pending_payment"): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/admin/organization/${orgId}/status`, {
            method: "PUT",
            body: JSON.stringify({ status })
        });
    }

    static async updateBillingDetails(orgId: string, billingDetails: {
        companyName?: string;
        email?: string;
        street?: string;
        postcode?: string;
        city?: string;
        country?: string;
        kvkNumber?: string;
        vatNumber?: string;
    }): Promise<any> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}/billing-details`, {
            method: "PUT",
            body: JSON.stringify(billingDetails)
        });
    }

    static async getInvoices(orgId: string): Promise<any[]> {
        const apiInstance = await BaseApiService.getInstance();
        return await apiInstance.execute(`/api/v1/organization/${orgId}/invoices`, {
            method: "GET"
        });
    }
}



